import React, { useMemo, useState } from "react";

import { updateConsentManagementConfigVersion } from "@/consent-management/api/updateConsentManagementConfigVersion";
import {
  FreshpaintConsentManagerPreview,
  PREVIEW_DEBOUNCE_MS,
} from "@/consent-management/components/ConfigPage/ConsentManagerConfigForm/ConfigContent/FreshpaintConfigContent/sections/FreshpaintConsentManagerPreview/FreshpaintConsentManagerPreview";
import {
  isValidPrivacyPolicyUrl,
  PRIVACY_POLICY_URL_ERROR_MESSAGE,
} from "@/consent-management/components/ConfigPage/ConsentManagerConfigForm/schema";
import { draftValuesToPreviewConfig } from "@/consent-management/components/ConfigPage/ConsentManagerConfigForm/utils";
import { Banner } from "@/design-system/components/Banner/Banner";
import { Input } from "@/design-system/components/Input/Input";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import { Typography } from "@/design-system/components/Typography/Typography";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { unpackRequestErrorMessage } from "@/utils/request";
import { WizardLink } from "@/wizards/framework/WizardLink";
import { WizardStepLayout } from "@/wizards/framework/WizardStepLayout";
import { ConsentLoadError } from "../ConsentLoadError";
import { isWizardConfig, summarizeTargets } from "../targeting";
import { ConsentManagerStepProps } from "../types";
import {
  freshpaintConsent,
  LOAD_ERROR_REASON,
  NO_WRITE_ACCESS_REASON,
  useFreshpaintConsent,
} from "../useFreshpaintConsent";

export function BannerStep({
  data,
  updateData,
  goNext,
}: ConsentManagerStepProps) {
  const { draft, canWrite, isLoading, loadError, retry, fetchLatestDraft } =
    useFreshpaintConsent();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showUrlError, setShowUrlError] = useState(false);

  const savedUrl: string = draft?.freshpaintConfig?.privacyPolicyUrl ?? "";
  const savedLinkText: string =
    draft?.freshpaintConfig?.privacyPolicyLinkText ?? "";
  const privacyPolicyUrl = data.privacyPolicyUrl ?? savedUrl;
  const privacyPolicyLinkText = data.privacyPolicyLinkText ?? savedLinkText;
  const urlIsValid = isValidPrivacyPolicyUrl(privacyPolicyUrl.trim());

  const debouncedFields = useDebouncedValue(
    useMemo(
      () => ({ privacyPolicyUrl, privacyPolicyLinkText }),
      [privacyPolicyUrl, privacyPolicyLinkText],
    ),
    PREVIEW_DEBOUNCE_MS,
  );
  const previewConfig = useMemo(
    () =>
      draft
        ? draftValuesToPreviewConfig({
            ...draft,
            freshpaintConfig: {
              ...draft.freshpaintConfig,
              ...debouncedFields,
            },
          })
        : undefined,
    [draft, debouncedFields],
  );

  async function save() {
    if (!urlIsValid) {
      setShowUrlError(true);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const latest = await fetchLatestDraft();
      if (!latest) {
        throw new Error("Your consent banner draft couldn’t be found");
      }
      // Fields left untouched keep whatever the server has now, even if it
      // changed on the Consent Manager page after the wizard loaded.
      const edits = {
        ...(data.privacyPolicyUrl !== null && {
          privacyPolicyUrl: data.privacyPolicyUrl.trim(),
        }),
        ...(data.privacyPolicyLinkText !== null && {
          privacyPolicyLinkText: data.privacyPolicyLinkText.trim(),
        }),
      };
      if (
        Object.entries(edits).some(
          ([field, value]) => latest.freshpaintConfig?.[field] !== value,
        )
      ) {
        await updateConsentManagementConfigVersion({
          type: freshpaintConsent.slug,
          freshpaintConfig: { ...latest.freshpaintConfig, ...edits },
        });
      }
      updateData({ privacyPolicyUrl: null, privacyPolicyLinkText: null });
      goNext();
    } catch (saveError) {
      setError(unpackRequestErrorMessage(saveError));
      setSaving(false);
    }
  }

  return (
    <WizardStepLayout
      title="Link your privacy policy"
      description={
        summarizeTargets(draft).some(isWizardConfig)
          ? "The banner links to your privacy policy so visitors can read how you use their data before they choose. Every rule the wizard made uses this link unless its site has its own."
          : "The banner links to your privacy policy so visitors can read how you use their data before they choose."
      }
      primaryAction={{
        label: "Save and continue",
        onClick: save,
        loading: saving,
        disabled: loadError || !draft || canWrite === false,
        disabledReason: loadError
          ? LOAD_ERROR_REASON
          : canWrite === false
            ? NO_WRITE_ACCESS_REASON
            : undefined,
      }}
    >
      {loadError ? (
        <ConsentLoadError retry={retry} />
      ) : isLoading || !previewConfig ? (
        <LoadingSpinner className="size-6" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Privacy policy URL"
              placeholder="https://example.com/privacy"
              value={privacyPolicyUrl}
              onChange={(event) =>
                updateData({ privacyPolicyUrl: event.target.value })
              }
              onBlur={() => setShowUrlError(true)}
              error={
                urlIsValid || !showUrlError
                  ? undefined
                  : {
                      type: "validate",
                      message: PRIVACY_POLICY_URL_ERROR_MESSAGE,
                    }
              }
            />
            <Input
              label="Link text"
              placeholder="Privacy policy"
              value={privacyPolicyLinkText}
              onChange={(event) =>
                updateData({ privacyPolicyLinkText: event.target.value })
              }
            />
          </div>
          <div className="border-borderSubtle bg-surface-sunken rounded-lg border px-4 py-2">
            <FreshpaintConsentManagerPreview
              consentManagementConfig={previewConfig}
            />
          </div>
          <Typography variant="body-sm" color="subtle">
            Change the wording, colors, layout or region rules on the{" "}
            <WizardLink
              to={freshpaintConsent.configPageUrl()}
              className="text-textActionSelected cursor-pointer underline"
            >
              Consent Manager page
            </WizardLink>
            . The wizard picks up those changes when you come back.
          </Typography>
        </>
      )}
      {error && <Banner type="error">{error}</Banner>}
    </WizardStepLayout>
  );
}
